import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getVacationMetric,
  setSchoolVacation,
  clearSchoolVacation,
  VacationResponse,
} from '../../services/api';
import MonitorLayout from './MonitorLayout';

// Per-school draft of the vacation window, seeded from the server and edited
// inline before saving.
type Draft = { start: string; end: string; label: string };

const PROVISIONAL_NOTE =
  'Provisional only in that the vacation window is set manually by the admin (the app ' +
  'has no official school calendar). The green "shared a project" figure is a real signal ' +
  'from the student Projects tab; the amber "used the coach" figure is a softer activity ' +
  'proxy. Both are scored against this same per-school window.';

const ProvisionalBadge: React.FC = () => (
  <span
    title={PROVISIONAL_NOTE}
    className="inline-flex cursor-help items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[0.7rem] font-bold text-amber-700"
  >
    ⚠ Provisional
  </span>
);

const fmtRange = (start?: string | null, end?: string | null): string => {
  if (!start || !end) return 'No window set';
  const f = (s: string) =>
    new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${f(start)} – ${f(end)}`;
};

const VacationProjects: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<VacationResponse | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busySchool, setBusySchool] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const onAuthError = (msg: string) => {
    if (/token|unauth/i.test(msg)) {
      localStorage.removeItem('monitorToken');
      navigate('/monitor/login', { replace: true });
    }
  };

  const load = () => {
    const token = localStorage.getItem('monitorToken');
    if (!token) {
      navigate('/monitor/login', { replace: true });
      return;
    }
    getVacationMetric(token)
      .then((d) => {
        setData(d);
        const seed: Record<string, Draft> = {};
        d.schools.forEach((s) => {
          seed[s.school] = { start: s.start_date || '', end: s.end_date || '', label: s.label || '' };
        });
        setDrafts(seed);
        setError(null);
      })
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        onAuthError(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const patch = (school: string, key: keyof Draft, value: string) =>
    setDrafts((d) => ({ ...d, [school]: { ...d[school], [key]: value } }));

  const save = async (school: string) => {
    const token = localStorage.getItem('monitorToken');
    if (!token) return;
    const d = drafts[school];
    if (!d?.start || !d?.end) {
      setError(`Pick both a start and end date for ${school}.`);
      return;
    }
    if (d.end < d.start) {
      setError(`End date must be on or after the start date for ${school}.`);
      return;
    }
    setBusySchool(school);
    setError(null);
    try {
      await setSchoolVacation(token, school, d.start, d.end, d.label || undefined);
      load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed.';
      setError(msg);
      onAuthError(msg);
    } finally {
      setBusySchool(null);
    }
  };

  const clear = async (school: string) => {
    const token = localStorage.getItem('monitorToken');
    if (!token) return;
    setBusySchool(school);
    setError(null);
    try {
      await clearSchoolVacation(token, school);
      load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Clear failed.';
      setError(msg);
      onAuthError(msg);
    } finally {
      setBusySchool(null);
    }
  };

  const schools = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    const list = term
      ? data.schools.filter((s) => s.school.toLowerCase().includes(term))
      : data.schools;
    // Schools with a window first (they carry a live number), then by name.
    return [...list].sort((a, b) => {
      const aw = a.pct != null ? 0 : 1;
      const bw = b.pct != null ? 0 : 1;
      return aw - bw || a.school.localeCompare(b.school);
    });
  }, [data, q]);

  const overall = data?.overall;

  return (
    <MonitorLayout>
      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Vacation projects</h1>
          <ProvisionalBadge />
        </div>
        <p className="mt-0.5 max-w-3xl text-sm text-slate-500">
          Of students at home over the vacation, how many worked on a project on their own? Set each
          school's window below — the green number is students who actually <strong>shared a project</strong>{' '}
          in the window (real signal); the amber number is anyone who <strong>used the coach</strong> at all
          (softer proxy). The student "Projects" tab feeds the green number.
        </p>
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : !data ? (
        <p className="py-10 text-center text-sm text-slate-400">No data.</p>
      ) : (
        <>
          {/* Overall (pooled across schools that have a window).
              Two headline numbers: activity (soft proxy) + projects (real). */}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-3">
              <p className="text-3xl font-bold leading-none tracking-tight text-emerald-700">
                {overall?.project_pct == null ? '—' : `${overall.project_pct}%`}
              </p>
              <p className="mt-1.5 text-[11px] font-medium leading-tight text-slate-600">
                Shared a project during vacation
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-3">
              <p className="text-3xl font-bold leading-none tracking-tight text-amber-700">
                {overall?.pct == null ? '—' : `${overall.pct}%`}
              </p>
              <p className="mt-1.5 text-[11px] font-medium leading-tight text-slate-600">
                Active on the coach (any use)
              </p>
            </div>
            <Tile
              value={String(overall?.projects_shared ?? 0)}
              label="Projects shared (in-window)"
              tint="bg-fuchsia-100 text-fuchsia-700"
            />
            <Tile
              value={String(overall?.active_students ?? 0)}
              label="Active students (in-window)"
              tint="bg-green-100 text-green-700"
            />
            <Tile
              value={String(overall?.total_students ?? 0)}
              label="Students in schools with a window"
              tint="bg-blue-100 text-blue-700"
            />
            <Tile
              value={`${overall?.schools_with_window ?? 0}/${overall?.schools_total ?? 0}`}
              label="Schools with a window set"
              tint="bg-violet-100 text-violet-700"
            />
          </div>

          {/* Per-school windows + result */}
          <div className="mt-5 rounded-2xl border border-[#E9E6F4] bg-white p-4 sm:p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-slate-900">Per-school vacation windows</h2>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search schools…"
                className="rounded-lg border border-[#E9E6F4] bg-white px-3 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
              />
            </div>

            {schools.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No schools match.</p>
            ) : (
              <div className="divide-y divide-[#F1EFFA]">
                {schools.map((s) => {
                  const d = drafts[s.school] || { start: '', end: '', label: '' };
                  const busy = busySchool === s.school;
                  const dirty =
                    d.start !== (s.start_date || '') ||
                    d.end !== (s.end_date || '') ||
                    d.label !== (s.label || '');
                  return (
                    <div key={s.school} className="flex flex-col gap-3 py-3.5 lg:flex-row lg:items-center lg:justify-between">
                      {/* Identity + live result: projects (real) + activity (proxy) */}
                      <div className="min-w-0 lg:w-72 lg:flex-none">
                        <p className="truncate text-sm font-semibold text-slate-800">{s.school}</p>
                        <p className="text-xs text-slate-400">
                          {s.total_students} students · {fmtRange(s.start_date, s.end_date)}
                        </p>
                        {s.pct != null && (
                          <div className="mt-1.5 space-y-1">
                            <StatBar
                              pct={s.project_pct ?? 0}
                              color="bg-emerald-500"
                              text="text-emerald-700"
                              caption={`${s.students_with_project ?? 0}/${s.total_students} shared · ${s.projects_shared ?? 0} projects`}
                            />
                            <StatBar
                              pct={s.pct}
                              color="bg-amber-500"
                              text="text-amber-700"
                              caption={`${s.active_students}/${s.total_students} used the coach`}
                            />
                          </div>
                        )}
                      </div>

                      {/* Editor */}
                      <div className="flex flex-wrap items-end gap-2">
                        <label className="flex flex-col text-[11px] font-medium text-slate-500">
                          Start
                          <input
                            type="date"
                            value={d.start}
                            onChange={(e) => patch(s.school, 'start', e.target.value)}
                            className="mt-0.5 rounded-lg border border-[#E9E6F4] bg-white px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                          />
                        </label>
                        <label className="flex flex-col text-[11px] font-medium text-slate-500">
                          End
                          <input
                            type="date"
                            value={d.end}
                            min={d.start || undefined}
                            onChange={(e) => patch(s.school, 'end', e.target.value)}
                            className="mt-0.5 rounded-lg border border-[#E9E6F4] bg-white px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                          />
                        </label>
                        <label className="flex flex-col text-[11px] font-medium text-slate-500">
                          Label (optional)
                          <input
                            type="text"
                            value={d.label}
                            placeholder="Summer 2026"
                            onChange={(e) => patch(s.school, 'label', e.target.value)}
                            className="mt-0.5 w-32 rounded-lg border border-[#E9E6F4] bg-white px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                          />
                        </label>
                        <button
                          type="button"
                          disabled={busy || !dirty}
                          onClick={() => save(s.school)}
                          className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {busy ? 'Saving…' : 'Save'}
                        </button>
                        {s.start_date && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => clear(s.school)}
                            className="rounded-lg border border-[#E9E6F4] bg-white px-3 py-2 text-xs font-semibold text-slate-500 shadow-sm hover:bg-slate-50 disabled:opacity-40"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-slate-400">
            {PROVISIONAL_NOTE}
          </p>
        </>
      )}
    </MonitorLayout>
  );
};

const StatBar: React.FC<{ pct: number; color: string; text: string; caption: string }> = ({
  pct,
  color,
  text,
  caption,
}) => (
  <div className="flex items-center gap-2">
    <span className="relative h-2 w-24 flex-none overflow-hidden rounded bg-slate-100">
      <span className={`absolute inset-y-0 left-0 rounded ${color}`} style={{ width: `${Math.max(2, pct)}%` }} />
    </span>
    <span className={`w-9 flex-none text-right text-xs font-bold tabular-nums ${text}`}>{pct}%</span>
    <span className="truncate text-[11px] text-slate-400">{caption}</span>
  </div>
);

const Tile: React.FC<{ value: string; label: string; tint: string }> = ({ value, label, tint }) => (
  <div className="rounded-xl border border-[#E9E6F4] bg-white px-3 py-2.5">
    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold ${tint}`}>•</span>
    <p className="mt-1.5 text-lg font-bold leading-none tracking-tight text-slate-900">{value}</p>
    <p className="mt-1 text-[11px] font-medium leading-tight text-slate-500">{label}</p>
  </div>
);

export default VacationProjects;
